import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  BaseChapterProps,
  BaseProfileFieldsValues,
  ProfileFormValues,
  ProofOfRegisteredAddressFieldsValues,
} from '@/pages/UserAccount/Profile';
import { FormHasDirtyFields } from '@/pages/UserAccount/Profile/ui/ProfilePresentational';

import { IntegrationSystem, SelectOption } from '@/shared/types';

import { DocumentTypeEntity, AddressType } from '@/api/types';

import BaseChapterTemplate from './BaseChapterTemplate/BaseChapterTemplate';
import { UniversalDocumentFields } from './UniversalDocumentFields';

interface ProofOfAddressDataProps extends BaseChapterProps {
  onSubmit: (values: ProfileFormValues) => void;
  regAddressDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null;
  setFormHasDirtyFields: (value: FormHasDirtyFields) => void;
  activeKey: Array<NonNullable<FormHasDirtyFields>>;
}

const proofOfAddressDataFields = [AddressType.REGISTRATION, 'documents'] satisfies Array<
  keyof ProofOfRegisteredAddressFieldsValues | keyof BaseProfileFieldsValues
>;

export const ProofOfAddressData: FC<ProofOfAddressDataProps> = ({
  onSubmit,
  onReset,
  regAddressDocumentTypeEntities,
  setFormHasDirtyFields,
  activeKey,
}) => {
  const {
    control,
    watch,
    handleSubmit,
    formState: { dirtyFields },
  } = useFormContext<ProfileFormValues>();

  const isProofOfAddressDataFieldsDirty = Object.keys(dirtyFields).some((key) =>
    proofOfAddressDataFields.includes(key as keyof Omit<ProofOfRegisteredAddressFieldsValues, 'registeredDocTypeCode'>),
  );

  useEffect(() => {
    if (!isProofOfAddressDataFieldsDirty) return setFormHasDirtyFields(null);

    setFormHasDirtyFields('proofRegistered');
  }, [isProofOfAddressDataFieldsDirty]);

  useEffect(() => {
    if (!activeKey.includes('proofRegistered') && isProofOfAddressDataFieldsDirty) onReset();
  }, [activeKey]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <BaseChapterTemplate
        fields={[
          <UniversalDocumentFields
            fieldName={AddressType.REGISTRATION}
            key="documentFields"
            disabled
            documentTypeEntities={regAddressDocumentTypeEntities as SelectOption[]}
            layout="profile"
            control={control}
            watch={watch}
          />,
        ]}
        isDisabledSubmitAction={isProofOfAddressDataFieldsDirty}
        onReset={onReset}
      />
    </form>
  );
};
